import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import Paper from '@base/Paper/Paper';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import H2 from '@base/H2/H2';
import { useDispatchToStore, useSelectorToStore } from '@hooks/reduxHooks';
import useSubscribed from '@hooks/useSubscribed';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import breadcrumbs, { goBack } from '@utils/breadcrumbs';
import { fetchTopologyFeed } from '@actions/topologies';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import { Spacings, mediaQuery, Fonts } from '@styles/index';

import { PlusTopologySource } from '@constants/index';
import { getIsLoggedIn } from '@selectors/auth';
import { getTopologyFeed, getTopologyInfo } from '@selectors/syllabus';
import {
  getIsSubscribedToLite,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import PreSubscriptionContent from './PreSubscriptionContent';
import FeedBlockContainer from './FeedBlockContainer';

const HeaderNavBar = styled(Paper)`
  margin-bottom: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_12B} ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    background: var(--color-base-0);
    box-shadow: none;
    padding: ${Spacings.SPACING_6B} 0 0 0;
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const Title = styled(H2)`
  margin-top: ${Spacings.SPACING_6B};
  height: 38px;
  font-size: ${Fonts.H2};
`;

const getFeedBlockKey = (feed) => feed.uid;

const PlusTopologyContent = ({ source, topologyFeedUID, itemExtraProps }) => {
  const dispatch = useDispatchToStore();
  const topologyFeed = useSelectorToStore((state) =>
    getTopologyFeed(state, topologyFeedUID)
  );

  const loadMoreFeedBlocks = () => {
    return dispatch(
      fetchTopologyFeed({
        topologyUid: topologyFeedUID,
        pageSource: source,
        nextPage: true
      })
    );
  };

  return (
    <InfiniteList
      data={topologyFeed}
      loadMore={loadMoreFeedBlocks}
      RenderItem={FeedBlockContainer}
      getItemKey={getFeedBlockKey}
      extraProps={itemExtraProps}
      gridColumn="span 18"
    />
  );
};

PlusTopologyContent.propTypes = {
  source: PropTypes.string.isRequired,
  topologyFeedUID: PropTypes.string.isRequired,
  itemExtraProps: PropTypes.shape({
    goalUID: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired
  }).isRequired
};

const getHeaderTitle = (goalTopology, isSyllabusPage, topology) => {
  let headerTitle = `${goalTopology.name} ${plusNavBarItems.PLAYLIST}`;
  if (goalTopology.isNoTestGoal) {
    headerTitle = `${goalTopology.name} Key concepts`;
  }
  if (goalTopology.isRecordedGoal && isSyllabusPage) {
    return 'Video lessons';
  }
  if (!isSyllabusPage) {
    headerTitle = `${goalTopology.name} ${topology.name}` || '';
  }
  return headerTitle;
};

const headerTags = (goalTopology, source, isOfflineSubscription) => {
  const tags = [];
  if (goalTopology) {
    tags.push(
      ...breadcrumbs.getPlusTopicsTags(goalTopology, isOfflineSubscription)
    );
    if (source === PlusTopologySource.TOPIC_FEED) {
      tags.push(...breadcrumbs.getTopics(goalTopology));
    }
  }
  return tags;
};

const PlusTopology = ({ source, goalUID, topologyUID }) => {
  const goalTopology = useSelector((state) => getTopologyInfo(state, goalUID));
  const topology = useSelector((state) => getTopologyInfo(state, topologyUID));
  const dispatch = useDispatchToStore();
  const isSubscribed = useSubscribed(goalUID);
  const isLiteUser = useSelector(getIsSubscribedToLite(goalUID));
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isSyllabusPage = source === PlusTopologySource.TOPICS;
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));

  // refresh feed after login
  useFirstRenderSkip(() => {
    dispatch(
      fetchTopologyFeed({
        topologyUid: topology.uid,
        nextPage: true,
        pageSource: source,
        resetWithoutEmpty: true,
        params: {
          timestamp: Date.now()
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const headerTitle = getHeaderTitle(goalTopology, isSyllabusPage, topology);

  const renderPreSubscriptionContent = () => {
    if (isLiteUser) return null;

    return (
      <PreSubscriptionContent
        source={source}
        goalUID={goalUID}
        topology={topology}
        headerTitle={headerTitle}
      />
    );
  };

  return (
    <>
      <HeaderNavBar gridColumn="span 18">
        <BreadCrumbs
          tags={headerTags(goalTopology, source, isOfflineSubscription)}
          onBackClick={goBack}
          useBackClick
        />
        <Title as="h1" lineClamp={1}>
          <span title={headerTitle}>{headerTitle}</span>
        </Title>
      </HeaderNavBar>
      {!isSubscribed && renderPreSubscriptionContent()}
      <PlusTopologyContent
        source={source}
        topologyFeedUID={topology.uid}
        itemExtraProps={{ goalUID, source }}
      />
    </>
  );
};

const sources = Object.keys(PlusTopologySource).map(
  (key) => PlusTopologySource[key]
);

PlusTopology.propTypes = {
  source: PropTypes.oneOf(sources).isRequired,
  goalUID: PropTypes.string.isRequired,
  topologyUID: PropTypes.string.isRequired
};

export default PlusTopology;
